import { useToast } from '@laam/ui/base';
import { useCheckoutStore } from '~/stores/checkout';
import { useCountryCode } from '~/stores/country';
import { useCurrency } from '~/stores/currency';
import { useGlobalStore } from '~/stores/global';
import { useLoganStore } from '~/stores/logan';
import { useEasyPaisaStore } from '~/stores/easypaisa';
import {
	generatePurchaseEventData,
	generateThankyouObject,
	getUsdTotal,
	transformPhoneNumber,
} from '~/utils/checkout';
import countries from '~/utils/countries.json';
import { convertQueryStringToObject } from '~/utils/helpers';
import { EASYPAISA, PAYMENT_METHODS_NEW } from './enums';
import type { PaymentResult } from '~/utils/easypaisa-payment';

export const completeEasyPaisaOrder = async (
	paymentResponse: PaymentResult,
) => {
	const EasyPaisaStore = useEasyPaisaStore();
	const CheckoutStore = useCheckoutStore();
	const LoganStore = useLoganStore();
	const { $conversionRates } = useGlobalStore();

	const { completeOrderAsync } = CheckoutStore;
	const { $eventClient, $cathodeClient } = useNuxtApp();
	const { currencyCode } = storeToRefs(useCurrency());
	const ipCountryCode = useCountryCode().countryCode;
	const { toast } = useToast();

	try {
		if (!paymentResponse || !EasyPaisaStore.intentId) {
			throw new Error(`Missing payment response or intent ID`);
		}

		EasyPaisaStore.isProcessingPayment = true;

		const {
			checkoutData,
			subTotal,
			rewardsRatesData,
			rewardsToBeRedeemed,
			values,
			tax,
			shipping,
			grandTotal,
			countryCodeRef,
			contactInformation,
			couponCodeDiscountInPkr,
			usingWallet,
			potentialRewards,
			discountObject,
			checkoutHash
		} = storeToRefs(CheckoutStore);

		const { updateInputDisabled, setPlaceOrderLoading, setLoading } =
			CheckoutStore;
		const { loganId } = storeToRefs(LoganStore);
		const { path } = useRoute();

		if (paymentResponse.paymentStatus !== 'S') {
			toast({
				title: 'The payment could not go through. Please try again',
				duration: 3000,
			});
			setLoading(false);

			$eventClient.sendEvent('card-payment-failure', {
				success: false,
				paymentMethod: PAYMENT_METHODS_NEW.EASYPAISA,
				subTotal: subTotal.value,
				shipping: shipping.value!,
				totalTax: tax.value!,
				discount: 0,
				promotionAmount: 0,
				creditCardDiscount: 0,
				loganId: loganId.value,
				checkoutHash: checkoutHash.value,
				message: `EasyPaisa payment failed with status: ${paymentResponse.status}`,
				error: true,
				checkoutRedesign: true,
			});

			$eventClient.sendEvent('checkout-errors', {
				path: path,
				errorTrace: 'Failed placing easypaisa order',
				error: `EasyPaisa payment failed with status: ${paymentResponse.status}`,
				checkoutRedesign: true,
			});

			return;
		}

		const completeOrderPayload = {
			hash: checkoutHash.value,
			intentId: EasyPaisaStore.intentId,
			paymentType: EASYPAISA,
			discountsObject: {
				discounts: Object.values(discountObject.value),
			},
			potentialRewards: potentialRewards.value?.toString() || '0',
			baseCurrencyCode: currencyCode.value,
		};

		setLoading(true);
		const data = await completeOrderAsync(completeOrderPayload);
		updateInputDisabled(false);
		setPlaceOrderLoading(true);

		if (data && [402, 413].includes(data.statusCode ?? 200)) {
			toast({
				title: 'The payment could not go through. Please try again',
				duration: 3000,
			});
			setLoading(false);

			$eventClient.sendEvent('card-payment-failure', {
				success: false,
				paymentMethod: PAYMENT_METHODS_NEW.EASYPAISA,
				subTotal: subTotal.value,
				shipping: shipping.value!,
				totalTax: tax.value!,
				discount: 0,
				promotionAmount: 0,
				creditCardDiscount: 0,
				loganId: loganId.value,
				checkoutHash: checkoutHash.value,
				message: data.detail,
				error: true,
				checkoutRedesign: true,
			});

			$eventClient.sendEvent('checkout-errors', {
				path: path,
				errorTrace: 'Failed placing easypaisa order',
				error: data.detail!,
				checkoutRedesign: true,
			});

			setLoading(false);
			return;
		}

		const thankyouObj = generateThankyouObject(data, checkoutHash.value);
		sessionStorage.setItem('orderData', JSON.stringify(thankyouObj));

		const purchaseEventData = generatePurchaseEventData(
			grandTotal.value,
			'PKR',
			PAYMENT_METHODS_NEW.EASYPAISA,
			0,
			parseFloat(data.tax!)!,
			parseFloat(data.shipping!),
			data.order_name!,
			data.order?.toString()!,
		);

		const computedNumber = computed(() => {
			let transformedPhNum;
			if (countryCodeRef.value === 'PK') {
				transformedPhNum =
					countries[
						countryCodeRef.value as keyof typeof countries
					].phone[0]?.toString()! +
					transformPhoneNumber(contactInformation.value?.phoneNumber?.trim()!);
			} else {
				transformedPhNum =
					countries[
						countryCodeRef.value as keyof typeof countries
					].phone[0]?.toString()! +
					contactInformation.value?.phoneNumber?.trim();
			}
			return transformedPhNum;
		});

		const usingRewards = computed(() => values.value.usingRewards);

		const convertRewardsToCoins = (rewardsToBeRedeemed: number) => {
			if (LoganStore.isSignedIn && rewardsRatesData.value) {
				return (
					rewardsToBeRedeemed /
					rewardsRatesData.value![currencyCode.value]!.coins_to_native
				);
			}
			return rewardsToBeRedeemed;
		};

		const purchaseCheckoutLocalizationPayload = {
			country: ipCountryCode,
			nativeTotalPrice: grandTotal.value,
			chargedTotalPrice:
				currencyCode.value !== 'pkr'
					? getUsdTotal(currencyCode.value, grandTotal.value)
					: grandTotal.value,
			chargedCurrency:
				currencyCode.value === 'pkr' && ipCountryCode === 'PK' ? 'pkr' : 'usd',
			nativeToUsdRate:
				$conversionRates[currencyCode.value]?.exchange_rate_to_usd,
			nativeToPkrRate: $conversionRates[currencyCode.value]?.default_rate,
			rewardsAmountInNative: rewardsToBeRedeemed.value,
		};

		$eventClient.sendEvent(
			'purchase',
			{
				...purchaseEventData,
				...convertQueryStringToObject(sessionStorage.getItem('campaign') || ''),
				lineItems: [],
				email: contactInformation.value?.email,
				phone: `${computedNumber.value}`,
				frontendTax: tax.value,
				frontendShipping: shipping.value,
				frontendSubtotal: subTotal.value,
				frontendGrandtotal: grandTotal.value,
				usingRewards: usingRewards.value,
				rewardsAmount: usingRewards.value
					? convertRewardsToCoins(rewardsToBeRedeemed.value).toString()
					: '0',
				...purchaseCheckoutLocalizationPayload,
				couponDiscount: couponCodeDiscountInPkr.value,
				checkoutRedesign: true,
				deliveryMethod: values.value.deliveryMethod,
				order_id: data.order!.toString(),
				items: checkoutData.value.map((item) => ({
					id: item.id,
					quantity: item.quantity,
				})),
				content_ids: checkoutData.value.map((item) => item.id),
				content_type: [],
				contents: checkoutData.value.map((item) => ({
					id: item.id,
					quantity: item.quantity,
				})),
				first_name: values.value.shippingAddress?.firstName,
				last_name: values.value.shippingAddress?.lastName,
			},
			LoganStore.isHvc ? ['savyour'] : undefined,
		);

		$cathodeClient.queueEvent('purchase', purchaseEventData);

		setLoading(false);
		if (usingWallet.value) {
			sessionStorage.setItem('wallet', 'true');
		}

		await navigateTo('/checkoutv2-thankyou');
	} catch (error) {
		const { toast } = useToast();
		const CheckoutStore = useCheckoutStore();

		CheckoutStore.setLoading(false);
		CheckoutStore.updateInputDisabled(false);

		toast({
			title: 'An error occurred while processing your order. Please try again.',
			duration: 3000,
		});

		const { $eventClient } = useNuxtApp();
		const { path } = useRoute();
		$eventClient.sendEvent('checkout-errors', {
			path: path,
			errorTrace: 'Exception in easypaisa order completion',
			error: error instanceof Error ? error.message : 'Unknown error',
			checkoutRedesign: true,
		});
	} finally {
		EasyPaisaStore.isProcessingPayment = false;
	}
};
